import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { v4 as uuid } from "uuid";

import config from "../../config";

const { isPlayerSupported, create: createMediaPlayer, PlayerState, PlayerEventType } = window.IVSPlayer;
const { ENDED, PLAYING, READY, BUFFERING, IDLE } = PlayerState;
const { ERROR } = PlayerEventType;

const usePlayer = (id, isPlayerVisible, isPlayerActive, setIsLoading) => {
  const player = useRef(null);
  const video = useRef();
  const pid = useRef(`${id} (${uuid().slice(-3)})`);
  const videoState = useRef("Idel");
  const isActive = useRef(isPlayerActive);
  const isVisible = useRef(isPlayerVisible);

  const {
    muted,
    paused,
    loading,
    toggleMute,
    play,
    sliderPlay,
    pause,
    sliderPause,
    togglePlayPause,
    setLoading,
    resetControls,
    jumpTo,
    setVisibleState,
  } = usePlayerControls(player, isVisible);

  // Handle case when autoplay with sound is blocked by browser
  useEffect(() => {
    if (loading || !player.current) return;
    // toggleMute(player.current.isMuted());
  }, [loading]);

  // Generic PlayerState event listener
  const onStateChange = useCallback(() => {
    const newState = player.current.getState();
    videoState.current = newState;
    setLoading(newState !== PLAYING);

    if (isVisible.current) {
      // setIsLoading(newState !== PLAYING && newState !== IDLE && newState !== READY);
    }
    if (newState === ENDED) {
      if (isVisible.current) {
        player.current.pause();
        player.current.seekTo(0);
        player.current.play();
      }
    }
  }, [setLoading]);

  // Generic PlayerEventType event listener
  const onError = useCallback((err) => {
    console.warn(`Player ${pid.current} Event - ERROR:`, err, player.current);
  }, []);

  const destroy = useCallback(() => {
    if (!player.current) return;

    // remove event listeners
    player.current.removeEventListener(IDLE, onStateChange);
    player.current.removeEventListener(READY, onStateChange);
    player.current.removeEventListener(PLAYING, onStateChange);
    player.current.removeEventListener(BUFFERING, onStateChange);
    player.current.removeEventListener(ENDED, onStateChange);
    player.current.removeEventListener(ERROR, onError);

    // delete and nullify player
    player.current.pause();
    player.current.delete();
    player.current = null;
    video.current.removeAttribute("src"); // remove possible stale src

    // reset player state controls to initial values
    resetControls();
  }, [onError, onStateChange, resetControls]);

  const create = useCallback(() => {
    if (!isPlayerSupported) return;

    // If a player instnace already exists, destroy it before creating a new one
    if (player.current) destroy();

    player.current = createMediaPlayer();
    video.current.crossOrigin = "anonymous";
    player.current.attachHTMLVideoElement(video.current);

    player.current.addEventListener(IDLE, onStateChange);
    player.current.addEventListener(READY, onStateChange);
    player.current.addEventListener(PLAYING, onStateChange);
    player.current.addEventListener(BUFFERING, onStateChange);
    player.current.addEventListener(ENDED, onStateChange);
    player.current.addEventListener(ERROR, onError);
  }, [destroy, onError, onStateChange]);

  const load = useCallback(
    (playbackUrl) => {
      if (!player.current) create();
      player.current.load(playbackUrl + "#t=0.001");
      if (config.PLAY_IN_BACKGROUND) play();
    },
    [create, play]
  );

  const log = (...messages) => {
    console.log(`Player ${pid.current}:`, ...messages);
  };

  return {
    player: player.current,
    pid: pid.current,
    togglePlayPause,
    toggleMute,
    destroy,
    loading,
    paused,
    muted,
    video,
    videoState,
    load,
    play,
    sliderPlay,
    pause,
    sliderPause,
    log,
    jumpTo,
    setVisibleState,
  };
};

const usePlayerControls = (player, isVisible) => {
  const [muted, setMuted] = useState(true);
  const [paused, setPaused] = useState(false);
  const [loading, setLoading] = useState(true);

  const sliderPlay = useCallback(() => {
    if (!player.current) return;

    if (player.current.isPaused()) {
      player.current.play();
    }
  }, [player]);

  const play = useCallback(() => {
    if (!player.current) return;

    if (player.current.isPaused()) {
      player.current.play();
      setPaused(false);
    }
  }, [player]);

  const pause = useCallback(() => {
    if (!player.current) return;

    if (!player.current.isPaused()) {
      player.current.pause();
      setPaused(true);
    }
  }, [player]);

  const sliderPause = useCallback(() => {
    if (!player.current) return;

    if (!player.current.isPaused()) {
      player.current.pause();
      // setPaused(true);
    }
  }, [player]);

  const togglePlayPause = useCallback(() => {
    if (!player.current) return;

    player.current.isPaused() ? play() : pause();
  }, [pause, play, player]);

  const toggleMute = useCallback(
    (mute = false) => {
      if (!player.current) return;
      player.current.setMuted(mute);
      setMuted(mute);
    },
    [player]
  );

  const jumpTo = useCallback(
    (time) => {
      if (!player.current) return;
      player.current.seekTo(time);
    },
    [player]
  );

  const setVisibleState = useCallback((state) => {
    if (!player.current) return;
    isVisible.current = state;
  });

  const resetControls = useCallback(() => {
    setMuted(true);
    setPaused(false);
    setLoading(true);
  }, []);

  return useMemo(
    () => ({
      muted,
      paused,
      loading,
      toggleMute,
      play,
      sliderPlay,
      pause,
      sliderPause,
      togglePlayPause,
      setLoading,
      resetControls,
      jumpTo,
      setVisibleState,
    }),
    [loading, muted, pause, sliderPause, paused, play, sliderPlay, toggleMute, togglePlayPause, resetControls, jumpTo, setVisibleState]
  );
};

export default usePlayer;
